<template>
  <div>
    <v-form ref="form">
      <v-row>
        <v-col cols="4" class="pt-0 pb-0 ma-0">
          <ClarionDateControl
            class="input-text-size"
            label="Business Date"
            v-model="businessDate"
            :isToggle="false"
            :rules="reqdDateValidation"
          />
        </v-col>
        <v-col cols="4" class="pt-0 pb-0 ma-0">
          <v-select
            class="input-text-size"
            label="WKST"
            :items="paymentWkstNumList"
            v-model="selectedWkstItem"
            :rules="reqdValidation"
          />
        </v-col>
        <v-col cols="4" class="pt-0 pb-0 ma-0">
          <v-select
            class="input-text-size"
            label="Select Import File"
            v-model="selectedImportDate"
            :items="importDateItems"
            item-text="label"
            item-value="value"
            :rules="reqdValidation"
          >
            <template v-slot:item="{ item }">
              <div class="payrix-item-row">
                <v-icon
                  v-if="item.state === 'imported'"
                  color="success"
                  small
                  class="mr-2"
                >mdi-check-circle</v-icon>
                <v-icon
                  v-else-if="item.state === 'partial'"
                  color="warning"
                  small
                  class="mr-2"
                >mdi-alert-circle</v-icon>
                <span :class="batchStateClass(item)">{{ item.label }}</span>
              </div>
            </template>
            <template v-slot:selection="{ item }">
              <v-icon
                v-if="item.state === 'imported'"
                color="success"
                small
                class="mr-1"
              >mdi-check-circle</v-icon>
              <v-icon
                v-else-if="item.state === 'partial'"
                color="warning"
                small
                class="mr-1"
              >mdi-alert-circle</v-icon>
              <span :class="batchStateClass(item)">{{ item.label }}</span>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-form>
    <v-row class="mt-4">
      <v-btn class="input-text-size flex"
        @click="importPayrixData"
        text
        color="primary"
        small
      >
        Import
      </v-btn>
    </v-row>
    <v-overlay :value="overlay">
      <v-progress-circular :size="50" color="primary" indeterminate />
    </v-overlay>
  </div>
</template>

<script>
import ClarionDateControl from '@/components/common/ClarionDateControl.vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import { clients } from '../../../../../util/clients';
import { generateQueryString } from '../../../../common/base/report/report-util';

const { backendRest } = clients.direct;

// How many recent calendar dates the dropdown offers. Batch dates older than
// this still appear if webtaxccpayments actually holds rows for them (see
// importDateItems), so shrinking the window cannot hide pending work.
const CALENDAR_DATE_COUNT = 31;

// batchDate is persisted as 'YYYY-MM-DD'; the dropdown speaks 'MM/DD/YYYY'.
// Split the string rather than round-tripping through Date, which would shift
// the day for anyone east of UTC.
const toDisplayDate = (isoDate) => {
  const [yyyy, mm, dd] = String(isoDate || '').split('-');
  return yyyy && mm && dd ? `${mm}/${dd}/${yyyy}` : null;
};

export default {
  components: {
    ClarionDateControl,
  },
  props: {
    baseCrudKey: {
      type: String,
    },
    parentBusinessDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      businessDate: null,
      selectedWkstItem: null,
      selectedImportDate: null,
      calendarDates: [],
      batchStatuses: [],
      overlay: false,
    };
  },
  mounted() {
    this.setDefaultData();
    this.calendarDates = Array.from({ length: CALENDAR_DATE_COUNT }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    });
    this.fetchBatchStatuses();
  },
  computed: {
    ...mapState({
      paymentWkstNumList: (state) => state.tax.taxPayment.paymentWkstNumList || [],
    }),
    ...mapGetters('base/crud', ['criteria']),
    criteriaState() {
      return this.criteria(this.baseCrudKey);
    },
    // Three states, not two. The import only flags records that passed
    // validation, so a batch date can be PARTIALLY imported — and the leftovers
    // are precisely the mismatched/skipped records somebody still has to deal
    // with. Collapsing partial into "imported" would hide them.
    importDateItems() {
      const statusByDate = {};
      this.batchStatuses.forEach((row) => {
        const display = toDisplayDate(row.batchDate);
        if (display) statusByDate[display] = row;
      });
      // Calendar dates give the recent days whether or not the nightly pre-pull
      // found anything; the backend's batch dates add back anything older that
      // is still sitting in webtaxccpayments.
      const dates = [...new Set([...this.calendarDates, ...Object.keys(statusByDate)])];
      return dates
        .sort((a, b) => new Date(b) - new Date(a))
        .map((value) => {
          const status = statusByDate[value];
          const total = (status && status.total) || 0;
          const imported = (status && status.imported) || 0;
          const item = {
            value, total, imported, pending: total - imported,
          };
          // No importable rows at all — nothing was collected that day. Most of
          // the calendar-generated dates land here.
          if (total === 0) {
            return { ...item, state: 'noData', label: `${value} - no data to import` };
          }
          if (imported >= total) {
            return { ...item, state: 'imported', label: value };
          }
          if (imported > 0) {
            return { ...item, state: 'partial', label: `${value} - ${imported} of ${total} imported` };
          }
          return { ...item, state: 'pending', label: value };
        });
    },
    reqdValidation() {
      return [(v) => !!v || 'Required'];
    },
    reqdDateValidation() {
      return [
        (v) => !!v || 'Required',
        this.validateDateWithinFiveDays,
      ];
    },
  },
  watch: {
    parentBusinessDate(newVal) {
      if (newVal !== this.businessDate) {
        this.businessDate = newVal;
      }
    },
  },
  methods: {
    ...mapActions(['reAuth', 'flashError', 'flashWarn', 'flashSuccess']),
    ...mapActions('SystemConfig', ['loadConfig']),
    batchStateClass(item) {
      return {
        'font-weight-medium success--text': item.state === 'imported' || item.state === 'noData',
        'font-weight-medium warning--text': item.state === 'partial',
      };
    },
    async fetchBatchStatuses() {
      try {
        const { jwt } = await this.reAuth();
        const queryString = generateQueryString([`token=${jwt}`]);
        const response = await backendRest.get(`/tax/ccPayments/payrix/imported-batches${queryString}`);
        this.batchStatuses = response.data || [];
      } catch (err) {
        // A failed status lookup must not block the import itself — every date
        // just falls back to looking un-imported.
        console.error('Error fetching Payrix batch import status:', err);
      }
    },
    async wkstTaxsetting() {
      const taxConfig = await this.loadConfig({ requiredScope: 'taxConfig' });
      return (taxConfig && taxConfig[0]) || [];
    },
    async setDefaultData() {
      const wkstTaxSetting = await this.wkstTaxsetting();
      const wkst = wkstTaxSetting.configuration.webPaymentNo !== ''
        ? Number(wkstTaxSetting.configuration.webPaymentNo)
        : this.criteriaState.criteriaWkst[0];
      this.businessDate = this.parentBusinessDate || this.criteriaState.postingDate;
      this.selectedWkstItem = wkst || null;
    },
    validateDateWithinFiveDays(value) {
      if (!value) return true;
      try {
        const entered = new Date(value);
        const baselineDate = this.parentBusinessDate || this.criteriaState.postingPayDate;
        if (!baselineDate) return true;
        const baseline = new Date(baselineDate);
        entered.setHours(0, 0, 0, 0);
        baseline.setHours(0, 0, 0, 0);

        const msPerDay = 1000 * 60 * 60 * 24;
        const diffInMs = Math.abs(entered - baseline);
        const diffInDays = diffInMs / msPerDay;
        return diffInDays <= 5 || 'Please enter a date within +/- 5 days of the current business date';
      } catch (err) {
        return 'Invalid date format';
      }
    },
    // Spell out what the selected date already holds, so a re-import is a
    // deliberate choice rather than a surprise.
    confirmPrompt() {
      const date = this.selectedImportDate;
      const item = this.importDateItems.find((i) => i.value === date);
      if (!item) return `Import Payrix data for ${date}?`;
      if (item.state === 'imported') {
        return `All ${item.total} transaction(s) for ${date} have already been imported. Import again anyway?`;
      }
      if (item.state === 'partial') {
        return `${item.imported} of ${item.total} transaction(s) for ${date} are already imported.`
          + ` Import the remaining ${item.pending}?`;
      }
      if (item.state === 'noData') {
        return `There are no Payrix transactions to import for ${date}. Continue anyway?`;
      }
      return `Import Payrix data for ${date} (${item.total} transaction(s))?`;
    },
    async importPayrixData() {
      if (!this.$refs.form.validate()) return;
      if (window.confirm(this.confirmPrompt())) {
        this.overlay = true;
        try {
          const { jwt } = await this.reAuth();
          const payload = {
            token: jwt,
            businessDate: new Date(this.businessDate),
            workstation: this.selectedWkstItem,
            batchDate: new Date(this.selectedImportDate),
          };
          const response = await backendRest.post('/tax/ccPayments/payrix/import', payload);
          if (response && response.data) {
            const {
              recordsImportedCount, recordsSkippedCount, importedRecords, skippedRecords,
            } = response.data;
            // The backend short-circuits with just a message when the batch date
            // has nothing left to import (a re-import of a finished date lands
            // here). Say so instead of rendering a summary PDF of nothing.
            if (recordsImportedCount === undefined && recordsSkippedCount === undefined) {
              this.flashWarn(response.data.message || 'No web payments found for the selected import file.');
              await this.fetchBatchStatuses();
              return;
            }
            const summaryCriteria = {
              recordsImportedCount,
              recordsSkippedCount,
              importedRecords,
              skippedRecords,
              batchDate: this.selectedImportDate,
            };
            const pdfResponse = await backendRest.post(
              '/tax/ccPayments/payrix/web-import-summary', // backend route-handler will generate AND email the pdf
              summaryCriteria,
              { responseType: 'blob' }, // Ensure response is treated as binary
            );
            // Open the PDF in a new browser tab
            const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            const summaryWindow = window.open(blobUrl, '_blank');
            if (!summaryWindow) {
              this.flashWarn('Pop-up blocked - the import succeeded but the summary PDF could not open. Allow pop-ups for this site and reload the file to view the summary.');
            }
            // Re-read the per-batch-date status so the dropdown reflects this
            // import right away (green, or amber if records were skipped).
            await this.fetchBatchStatuses();
            this.$emit('searchByCriteria');
          }
        } catch (err) {
          console.error('Error importing Payrix data or opening PDF summary:', err);
          this.flashError('Failed to import Payrix data.');
        } finally {
          this.overlay = false;
        }
      }
    },
  },
};
</script>

<style scoped>
  .flex {
    display: flex;
    justify-content: center;
    font-weight: bold;
  }
  .payrix-item-row {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 4px 0;
  }
</style>
